import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import AdSlot from '../../components/AdSlot';
import { setSeoMetadata } from '../../lib/seo';
import { useSiteFeatureFlags } from '../../hooks/useSiteFeatureFlags';
import { ROUTES } from '../../data/stjSite';

const IMG = {
  waterTaxi: 'https://images.unsplash.com/photo-1657205895605-e149ccabbd07?q=80&w=1200&auto=format&fit=crop',
  carBarge: 'https://images.unsplash.com/photo-1623068285724-3aa77cbd4b89?q=80&w=1200&auto=format&fit=crop',
  passengerFerry: 'https://images.unsplash.com/photo-1781512483011-2ebce64d656a?q=80&w=1200&auto=format&fit=crop',
  contactUs: 'https://images.unsplash.com/photo-1672841828271-54340a6fbcd3?q=80&w=1200&auto=format&fit=crop',
  westinFerry: 'https://images.unsplash.com/photo-1576513190925-b9429d643adc?q=80&w=1200&auto=format&fit=crop',
  jeepRentals: 'https://images.unsplash.com/photo-1504150955187-375abf343d45?q=80&w=1200&auto=format&fit=crop',
  islandTaxi: 'https://images.unsplash.com/photo-1757753465025-bf3ece73c5a9?q=80&w=1200&auto=format&fit=crop',
  publicTransport: 'https://images.unsplash.com/photo-1639448158266-80694db01f2b?q=80&w=1200&auto=format&fit=crop',
};

const ROW_ONE = [
  { key: 'water-taxi', title: 'Water Taxi', subtitle: 'Private Water Transportation', image: IMG.waterTaxi, href: ROUTES.waterTaxi },
  { key: 'car-barge', title: 'Car Barge', subtitle: 'Easy to Get Around Transportation', image: IMG.carBarge, href: ROUTES.carBarge },
];

const ROW_TWO = [
  { key: 'passenger-ferry', title: 'Passenger Ferry', subtitle: 'Island to Island Transportation', image: IMG.passengerFerry, href: ROUTES.passengerFerry },
  { key: 'contact-us', title: 'Contact Us', subtitle: "We're here to help plan your trip", image: IMG.contactUs, href: ROUTES.contactUs },
  { key: 'westin-ferry', title: 'Westin Ferry', subtitle: 'Private Resort Transportation', image: IMG.westinFerry, href: ROUTES.westinFerry },
];

const ROW_THREE = [
  { key: 'jeep-rentals', title: 'Jeep Rentals', subtitle: 'Rent Your Own Vehicle', image: IMG.jeepRentals, href: ROUTES.autoRentals },
  { key: 'island-taxi', title: 'Island Taxi', subtitle: 'Easy Ways to Get Around', image: IMG.islandTaxi, href: ROUTES.islandTaxi },
  { key: 'public-transportation', title: 'Public Transportation', subtitle: 'Public Island Transportation', image: IMG.publicTransport, href: ROUTES.publicTransportation },
];

/** One image tile with a dark-gradient overlay and white title/subtitle text. */
const Tile = ({ tile }) => (
  <Link
    to={tile.href}
    className="group relative block aspect-[4/3] overflow-hidden rounded-xl shadow-sm ring-1 ring-black/5"
    data-testid={`transport-tile-${tile.key}`}
  >
    <img
      src={tile.image}
      alt={tile.title}
      loading="lazy"
      className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
    />
    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
    <div className="absolute inset-x-0 bottom-0 p-4">
      <p className="text-base font-extrabold uppercase tracking-wide text-white sm:text-lg">{tile.title}</p>
      <p className="mt-0.5 text-xs text-white/85 sm:text-sm">{tile.subtitle}</p>
    </div>
  </Link>
);

const TransportationPage = () => {
  const { transportation_enabled, _loaded } = useSiteFeatureFlags();

  useEffect(() => {
    setSeoMetadata({
      title: 'Getting Around St John | Transportation Guide | St John USVI',
      description:
        'Everything you need to get around St John, USVI: water taxis, car barges, passenger ferries, the Westin ferry, jeep rentals, island taxis and public transportation (ViTran bus).',
      keywords:
        'st john transportation, car barge st john, passenger ferry st thomas st john, westin ferry, island taxi st john, vitran bus st john, jeep rentals st john',
      canonicalPath: '/island-info/transportation-st-john',
      ogType: 'website',
    });
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      <main className="pt-28 pb-20 lg:pt-32">
        <div className="mx-auto max-w-6xl px-4">
          <header className="mb-8 text-center">
            <h1 className="text-3xl font-extrabold uppercase italic tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
              Getting Around
            </h1>
            <p className="font-script mt-2 text-2xl text-cyan-500 sm:text-3xl">St John, Virgin Islands</p>
          </header>

          {!_loaded ? (
            <div className="flex justify-center py-16" data-testid="transportation-loading">
              <div className="h-10 w-10 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
            </div>
          ) : !transportation_enabled ? (
            <p className="py-16 text-center text-slate-500" data-testid="transportation-disabled">
              Our Transportation guide is being updated. Please check back soon.
            </p>
          ) : (
            <div className="space-y-4" data-testid="transportation-grid">
              <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
                <AdSlot id="transportation-top-left" className="aspect-[4/3]" />
                {ROW_ONE.map((tile) => <Tile key={tile.key} tile={tile} />)}
                <AdSlot id="transportation-top-right" className="aspect-[4/3]" />
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {ROW_TWO.map((tile) => <Tile key={tile.key} tile={tile} />)}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
                {ROW_THREE.map((tile) => <Tile key={tile.key} tile={tile} />)}
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default TransportationPage;
